import React from 'react';
import PropTypes from 'prop-types';
import reject from 'lodash/reject';
import StyledComponent from '~/components/Styled/Styled';
import userLocation from '~/util/userLocation';
import GameDetailsWeather from '~/constants/GameDetailsWeather';
import { defineMessages, intlShape, injectIntl } from 'react-intl';

import find from 'lodash/find';
import css from '~/components/GameDetailsCard/GameDetailsCard.scss';

const messages = defineMessages({
  attendance: {
    id: 'game_details.attendance',
    defaultMessage: 'Attendance',
  },
  broadcast: {
    id: 'game_details.broadcast',
    defaultMessage: 'Watch on',
  },
  closing_odds: {
    id: 'game_details.closing_odds',
    defaultMessage: 'Closing Odds',
  },
  linesman_one: {
    id: 'game_details.linesman_one',
    defaultMessage: 'Linesman 1',
  },
  linesman_two: {
    id: 'game_details.linesman_two',
    defaultMessage: 'Linesman 2',
  },
  location: {
    id: 'game_details.location',
    defaultMessage: 'Location',
  },
  officials: {
    id: 'game_details.officials',
    defaultMessage: 'Referees',
  },
  umpires: {
    id: 'game_details.umpires',
    defaultMessage: 'Umpires',
  },
  previous_matchup: {
    id: 'game_details.previous_matchup',
    defaultMessage: 'Previous Matchup',
  },
  referee: {
    id: 'game_details.referee',
    defaultMessage: 'Referee',
  },
  stadium: {
    id: 'game_details.stadium',
    defaultMessage: 'Stadium',
  },
  stadium_attendance: {
    id: 'game_details.stadium_attendance',
    defaultMessage: 'Attendance',
  },
});

const isWeatherForecast = detail => detail.identifier.includes('weather_forecast');

const includesIdentifier = unitIds => detail => unitIds.includes(detail.identifier);

const sortWeatherInfo = (detail1, detail2) => {
  if (detail1.identifier.includes('temperature')) return -1;
  if (detail1.identifier.includes('wind') && detail2.identifier.includes('precipitation')) {
    return -1;
  }
  return 1;
};

class GameDetailsCard extends React.Component {
  constructor() {
    super();

    this.state = {
      region: 'global',
    };
  }

  componentDidMount() {
    userLocation.promise.then(location => {
      this.setState({ region: location.countryCode.toLowerCase() });
    });
  }

  getLabel(detail) {
    const findLabel = (details, id) => {
      const detailObj = find(details, d => id === d.identifier);
      return detailObj ? detailObj.label : '';
    };

    const getOfficials = label => {
      switch (label) {
        case 'Umpires':
          return 'umpires';
        default:
          return 'officials';
      }
    };

    let formattedId = /broadcast/g.test(detail.identifier) ? 'broadcast' : detail.identifier;

    if (detail.identifier === 'officials') {
      formattedId = getOfficials(detail.label);
    }

    const { eventDetails, intl: { formatMessage } } = this.props;
    const label = messages[formattedId]
      ? formatMessage(messages[formattedId])
      : findLabel(eventDetails, detail.identifier);
    if (formattedId === 'broadcast') {
      const broadcastLocation = detail.identifier.split('/')[1].toUpperCase();
      return `${label} (${broadcastLocation})`;
    }
    return label;
  }

  render() {
    const { eventDetails, disableHeaderDetails, valuesAlignToRight } = this.props;

    const unitIds =
      this.state.region === 'us'
        ? GameDetailsWeather.IMPERIAL_FORECAST
        : GameDetailsWeather.METRIC_FORECAST;
    const firstDetail = eventDetails[0];
    const otherDetails = reject(
      disableHeaderDetails ? eventDetails : eventDetails.slice(1),
      isWeatherForecast
    );
    const weatherForecast = eventDetails
      .slice(1)
      .filter(isWeatherForecast)
      .filter(includesIdentifier(unitIds))
      .sort(sortWeatherInfo);
    return (
      <div className={css.root}>
        <div className={css.firstRow}>
          <h2 className={`${css.title} ${disableHeaderDetails ? '' : css.bottomTitleMargin}`}>
            Game Details
          </h2>
          {!disableHeaderDetails ? (
            <div className={css.row}>
              <div className={css.firstHeader}>{firstDetail.content}</div>
              <div className={css.firstDetail}>{firstDetail.label}</div>
            </div>
          ) : null}
          {weatherForecast.length > 0 ? (
            <div className={`row ${css.weatherRow}`}>
              {weatherForecast.map(forecast => (
                <div className={`col-xs-4 ${css.forecastItem}`} key={forecast.label}>
                  <div className={css.forecastContent}>{forecast.content}</div>
                  <div className={css.forecastHeader}>{forecast.label}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className={css.detailRows}>
          {otherDetails.map(detail => (
            <div className={css.row} key={detail.label}>
              <div className={css.label}>{`${this.getLabel(detail)}`}</div>
              <div className={`${css.content} ${valuesAlignToRight ? css.toRight : ''}`}>
                {detail.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

GameDetailsCard.propTypes = {
  valuesAlignToRight: PropTypes.bool,
  disableHeaderDetails: PropTypes.bool,
  eventDetails: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      content: PropTypes.string,
      identifier: PropTypes.string,
    })
  ).isRequired,
  intl: intlShape.isRequired,
};

GameDetailsCard.defaultProps = {
  disableHeaderDetails: false,
  valuesAlignToRight: false,
};

GameDetailsCard.displayName = 'GameDetailsCard';

export default injectIntl(StyledComponent(GameDetailsCard, [css]));
